/**
 * tps-rtw-customize.js  (v2 - full catalog scan, no search-query syntax)
 *
 * Instead of using Shopify's search query syntax (sku:"...") - which has
 * caused repeated quoting/parsing issues - this version pulls EVERY
 * product + variant SKU in the store via paginated Storefront API calls
 * (looping until there are no more pages), then does a plain JavaScript
 * string comparison (===) to find the match. No search syntax involved
 * anywhere, so there's nothing left to mis-parse.
 *
 * Trade-off: the first fetch pulls your whole catalog, which is slower
 * than a single targeted query. To keep this fast on repeat visits, the
 * result is cached in localStorage for 30 minutes.
 *
 * Config (shop domain, Storefront token) is read from data attributes on
 * #tps_customize_wrapper - set directly by Liquid.
 */
(function () {
  var TPS_CACHE_KEY = 'tps_all_variants_cache_v1';
  var TPS_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
  var TPS_PAGE_SIZE = 250; // Storefront API max per page
  var TPS_VARIANTS_PER_PRODUCT = 100; // generous cap per product

  var tpsLastCheckedSku = null;
  var tpsDebounceTimer = null;
  var tpsVariantIndexPromise = null; // in-flight fetch, so concurrent calls share one request

  function tpsStripRtwPrefix(sku) {
    if (!sku) return null;
    return sku.replace(/^RTW-/i, '');
  }

  function tpsGetWrapper() {
    return document.getElementById('tps_customize_wrapper');
  }

  function tpsGetButton() {
    return document.getElementById('tps_customize_btn');
  }

  function tpsGetLoader() {
    return document.getElementById('tps_customize_loader');
  }

  function tpsShowLoader() {
    var loader = tpsGetLoader();
    var btn = tpsGetButton();
    if (loader) loader.style.display = '';
    if (btn) btn.style.display = 'none';
  }

  function tpsHideLoader() {
    var loader = tpsGetLoader();
    if (loader) loader.style.display = 'none';
  }

  function tpsReadCache() {
    try {
      var raw = localStorage.getItem(TPS_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.timestamp || !parsed.variants) return null;
      if (Date.now() - parsed.timestamp > TPS_CACHE_TTL_MS) return null;
      return parsed.variants;
    } catch (e) {
      return null;
    }
  }

  function tpsWriteCache(variants) {
    try {
      localStorage.setItem(TPS_CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        variants: variants
      }));
    } catch (e) {
      console.warn('tps-rtw-customize: could not write cache', e);
    }
  }

  async function tpsFetchAllVariants(shopDomain, storefrontToken) {
    var allVariants = []; // { sku, handle }
    var cursor = null;
    var hasNextPage = true;
    var pageCount = 0;

    var query =
      'query TpsAllVariants($cursor: String) {' +
      '  products(first: ' + TPS_PAGE_SIZE + ', after: $cursor) {' +
      '    pageInfo { hasNextPage endCursor }' +
      '    edges {' +
      '      node {' +
      '        handle' +
      '        variants(first: ' + TPS_VARIANTS_PER_PRODUCT + ') {' +
      '          edges { node { sku } }' +
      '        }' +
      '      }' +
      '    }' +
      '  }' +
      '}';

    while (hasNextPage) {
      pageCount++;
      console.log('tps-rtw-customize: fetching product page', pageCount, cursor ? '(cursor: ' + cursor + ')' : '(first page)');

      var res = await fetch('https://' + shopDomain + '/api/2024-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken
        },
        body: JSON.stringify({
          query: query,
          variables: { cursor: cursor }
        })
      });

      if (!res.ok) {
        console.error('tps-rtw-customize: catalog fetch failed on page', pageCount, res.status, await res.text());
        break;
      }

      var json = await res.json();

      if (json.errors) {
        console.error('tps-rtw-customize: GraphQL errors on page', pageCount, json.errors);
        break;
      }

      var productsData = json.data && json.data.products;
      if (!productsData) {
        console.error('tps-rtw-customize: unexpected response shape', json);
        break;
      }

      productsData.edges.forEach(function (productEdge) {
        var handle = productEdge.node.handle;
        productEdge.node.variants.edges.forEach(function (variantEdge) {
          var sku = variantEdge.node.sku;
          if (sku) {
            allVariants.push({ sku: sku, handle: handle });
          }
        });
      });

      hasNextPage = productsData.pageInfo.hasNextPage;
      cursor = productsData.pageInfo.endCursor;
    }

    console.log('tps-rtw-customize: catalog scan complete -', allVariants.length, 'variants indexed across', pageCount, 'page(s)');
    return allVariants;
  }

  async function tpsGetVariantIndex(shopDomain, storefrontToken) {
    var cached = tpsReadCache();
    if (cached) {
      console.log('tps-rtw-customize: using cached catalog index -', cached.length, 'variants (refreshes every 30 min)');
      return cached;
    }

    // Share one in-flight fetch if called multiple times before it resolves
    if (!tpsVariantIndexPromise) {
      tpsVariantIndexPromise = tpsFetchAllVariants(shopDomain, storefrontToken).then(function (variants) {
        tpsWriteCache(variants);
        tpsVariantIndexPromise = null;
        return variants;
      }).catch(function (err) {
        tpsVariantIndexPromise = null;
        throw err;
      });
    }

    return tpsVariantIndexPromise;
  }

  async function tpsCheckAndUpdateButton() {
    var wrapper = tpsGetWrapper();
    var btn = tpsGetButton();
    if (!wrapper || !btn) return;

    var sku = btn.getAttribute('data-tps-sku');
    if (!sku || sku === tpsLastCheckedSku) return;
    tpsLastCheckedSku = sku;

    var targetSku = tpsStripRtwPrefix(sku);
    console.log('tps-rtw-customize: looking for exact match ->', targetSku);

    if (!targetSku) {
      tpsHideLoader();
      btn.style.display = 'none';
      btn.removeAttribute('data-tps-handle');
      return;
    }

    var shopDomain = wrapper.getAttribute('data-tps-shop-domain');
    var storefrontToken = wrapper.getAttribute('data-tps-storefront-token');

    if (!shopDomain || !storefrontToken) {
      console.warn('tps-rtw-customize: missing shop domain or storefront token');
      tpsHideLoader();
      return;
    }

    tpsShowLoader();

    var variants;
    try {
      variants = await tpsGetVariantIndex(shopDomain, storefrontToken);
    } catch (err) {
      console.error('tps-rtw-customize: catalog fetch failed', err);
      tpsHideLoader();
      return;
    }

    var match = variants.find(function (v) {
      return v.sku === targetSku;
    });

    console.log('tps-rtw-customize: match result ->', match || 'NO MATCH');

    // Re-fetch button/loader in case DOM was replaced while the request was in flight
    btn = tpsGetButton();
    tpsHideLoader();
    if (!btn) return;

    if (match) {
      btn.setAttribute('data-tps-handle', match.handle);
      btn.setAttribute('data-tps-target-sku', targetSku);
      btn.style.display = '';
    } else {
      btn.style.display = 'none';
      btn.removeAttribute('data-tps-handle');
      btn.removeAttribute('data-tps-target-sku');
    }
  }

  var tpsIsRedirecting = false;

  function tpsNormalize(value) {
    return (value || '').toString().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /**
   * Builds the Ring Builder URL. Uses the product's AJAX endpoint
   * (/products/{handle}.js) to find the variant that has:
   *   - the same sku-matched design (same shape/other options), AND
   *   - the same metal the customer selected on the RTW page.
   * If anything can't be resolved, falls back to the plain product URL
   * (the previous behaviour), so the button never breaks.
   */
  async function tpsResolveRingBuilderUrl(handle, targetSku, rtwMetal) {
    var baseUrl = '/products/' + handle;
    if (!targetSku || !rtwMetal) return baseUrl;

    try {
      var res = await fetch(baseUrl + '.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return baseUrl;
      var rbProduct = await res.json();

      // Find which option on the Ring Builder product is the metal
      var metalIndex = -1;
      (rbProduct.options || []).forEach(function (opt, i) {
        var name = (typeof opt === 'string' ? opt : opt && opt.name) || '';
        if (metalIndex === -1 && name.toLowerCase().indexOf('metal') !== -1) metalIndex = i;
      });
      if (metalIndex === -1) {
        console.warn('tps-rtw-customize: no "metal" option found on Ring Builder product', handle);
        return baseUrl;
      }

      var matchedVariant = rbProduct.variants.find(function (v) {
        return v.sku === targetSku;
      });
      if (!matchedVariant) return baseUrl;

      var wantedMetal = tpsNormalize(rtwMetal);
      var candidates = rbProduct.variants.filter(function (v) {
        if (tpsNormalize(v.options[metalIndex]) !== wantedMetal) return false;
        // every other option (shape, etc.) must match the sku-matched variant
        return matchedVariant.options.every(function (val, i) {
          return i === metalIndex || v.options[i] === val;
        });
      });

      console.log('tps-rtw-customize: metal', rtwMetal, '-> candidates', candidates);

      if (candidates.length) {
        // prefer an available variant if there are several
        var best = candidates.find(function (v) { return v.available; }) || candidates[0];
        return baseUrl + '?variant=' + best.id;
      }

      console.warn('tps-rtw-customize: no Ring Builder variant found for metal', rtwMetal);
      return baseUrl;
    } catch (err) {
      console.error('tps-rtw-customize: could not resolve metal variant', err);
      return baseUrl;
    }
  }

  document.addEventListener('click', async function (event) {
    var btn = event.target.closest('#tps_customize_btn');
    if (!btn) return;
    var handle = btn.getAttribute('data-tps-handle');
    if (!handle) return;
    if (tpsIsRedirecting) return; // ignore double clicks while resolving

    tpsIsRedirecting = true;
    // Safety net: never leave the button locked, even if navigation is slow/blocked
    setTimeout(function () { tpsIsRedirecting = false; }, 4000);

    var url;
    try {
      url = await tpsResolveRingBuilderUrl(
        handle,
        btn.getAttribute('data-tps-target-sku'),
        btn.getAttribute('data-tps-metal')
      );
    } catch (err) {
      url = '/products/' + handle;
    }
    window.location.href = url;
  });

  // Coming back with the browser Back button restores this page from the
  // back/forward cache with its JS state intact - unlock the button and
  // re-sync the selected variant so the next click works without a refresh.
  window.addEventListener('pageshow', function (event) {
    tpsIsRedirecting = false;
    if (event.persisted) {
      tpsSyncSelectedVariant();
    }
  });

  function tpsScheduleCheck() {
    clearTimeout(tpsDebounceTimer);
    tpsDebounceTimer = setTimeout(tpsCheckAndUpdateButton, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tpsCheckAndUpdateButton);
  } else {
    tpsCheckAndUpdateButton();
  }

  /**
   * Keeps data-tps-sku / data-tps-metal in sync with the variant currently
   * selected on the RTW page. This theme changes variants with its own
   * jQuery code (no Dawn re-render of our button), so the data attributes
   * rendered by Liquid go stale until refresh. Here we read the selected
   * variant id (same source the theme uses: input[name='id'], then the
   * ?variant= URL param) and look up its sku + metal from the current
   * product's /products/{handle}.js. Setting data-tps-sku triggers the
   * existing MutationObserver, which re-runs the match + loader as before.
   */
  var tpsRtwProductPromise = null;
  var tpsLastSyncedVariantId = null;

  function tpsGetSelectedVariantId() {
    var input = document.querySelector("input[name='id']");
    if (input && input.value) return String(input.value);
    var fromUrl = new URLSearchParams(window.location.search).get('variant');
    return fromUrl ? String(fromUrl) : null;
  }

  function tpsGetRtwProduct() {
    if (!tpsRtwProductPromise) {
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      if (!m) return Promise.resolve(null);
      tpsRtwProductPromise = fetch('/products/' + m[1] + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return tpsRtwProductPromise;
  }

  async function tpsSyncSelectedVariant() {
    var btn = tpsGetButton();
    if (!btn) return;

    var variantId = tpsGetSelectedVariantId();
    if (!variantId || variantId === tpsLastSyncedVariantId) return;

    var product = await tpsGetRtwProduct();
    if (!product) return;

    var variant = product.variants.find(function (v) { return String(v.id) === variantId; });
    if (!variant) return;

    tpsLastSyncedVariantId = variantId;

    // metal = option whose NAME contains "metal"; fall back to option2 (as the theme does)
    var metalIndex = 1;
    (product.options || []).forEach(function (opt, i) {
      var name = (typeof opt === 'string' ? opt : opt && opt.name) || '';
      if (name.toLowerCase().indexOf('metal') !== -1) metalIndex = i;
    });
    var metal = variant.options[metalIndex] || '';

    btn = tpsGetButton(); // re-fetch in case DOM changed during the request
    if (!btn) return;

    console.log('tps-rtw-customize: variant changed ->', variantId, '| sku:', variant.sku, '| metal:', metal);
    btn.setAttribute('data-tps-metal', metal);
    if (btn.getAttribute('data-tps-sku') !== variant.sku) {
      btn.setAttribute('data-tps-sku', variant.sku); // observer re-runs the match
    }
  }

  // remember the variant that was rendered on page load so we only act on changes
  tpsLastSyncedVariantId = tpsGetSelectedVariantId();

  // react quickly to clicks (theme updates the hidden id input ~700ms after a click)...
  ['click', 'change'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      setTimeout(tpsSyncSelectedVariant, 300);
      setTimeout(tpsSyncSelectedVariant, 1000);
    });
  });
  // ...plus a light safety net for any other way the variant can change
  setInterval(tpsSyncSelectedVariant, 1000);
  window.addEventListener('popstate', tpsSyncSelectedVariant);

  // Re-run whenever Dawn re-renders the ProductInfo block on variant change
  // (catalog index is cached, so this re-check is fast after the first load)
  var tpsObserver = new MutationObserver(function () {
    tpsScheduleCheck();
  });
  tpsObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-tps-sku']
  });
})();